import React, { useRef } from 'react';

const TheCalorimeter = () => {
    const containerRef = useRef(null);

    return (
        <section ref={containerRef} className="relative z-20 min-h-[96vh] overflow-visible px-6 pb-[clamp(4.5rem,8vw,8rem)] pt-[16vh] sm:pt-[18vh] md:min-h-[112vh] md:pt-[24vh]">
            {/* Ambient Background - Perfectly Synchronized with TheBeamline */}
            <div className="absolute inset-x-0 top-0 bottom-0 pointer-events-none z-0">
                {/* Large Subtle Fade-In - Eliminates the hard division */}
            <div className="absolute -top-[12vh] left-0 right-0 h-[30vh] bg-gradient-to-b from-transparent via-[#050505]/10 to-transparent z-10" />
                
                {/* Portal Reception Glow - Receives energy from previous section */}
            <div className="absolute -top-[14vw] left-1/2 -translate-x-1/2 h-[38vw] w-[108vw] rounded-[100%] bg-slate-300/[0.035] blur-[130px] mix-blend-screen md:-top-[18vw] md:h-[44vw] md:w-[100vw] md:blur-[170px]" />
            <div className="absolute top-[8vh] left-1/2 h-10 w-[62vw] -translate-x-1/2 rounded-full bg-slate-200/[0.02] blur-[84px] sm:h-12 sm:w-[54vw] sm:blur-[100px]" />
                
                <div className="absolute top-1/4 left-1/4 h-[50vw] w-[50vw] rounded-full bg-slate-200/[0.015] blur-[130px] mix-blend-screen md:blur-[150px]" />
                <div className="absolute bottom-1/4 right-1/4 hidden h-[50vw] w-[50vw] rounded-full bg-cyan-200/[0.016] blur-[150px] mix-blend-screen md:block" />
                
                {/* Parallax Scientific Layer - Aligned Grid */}
                <div className="absolute inset-0 opacity-[0.05]" 
                    style={{ 
                        backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(255,255,255,0.4) 1px, transparent 0)', 
                        backgroundSize: '80px 80px',
                        backgroundPosition: 'center top'
                    }} 
                />
                
            </div>

            <div className="relative z-10 mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 sm:gap-14 lg:grid-cols-2 lg:gap-24">
                <div>
                     <span className="home-kicker mb-4 block">The Engine</span>
                     <h2 className="mb-5 text-3xl font-bold tracking-tight text-white sm:text-4xl md:mb-6 md:text-5xl">Simplified on purpose.</h2>
                     <p className="mb-5 text-base font-light leading-relaxed text-gray-400 md:mb-6 md:text-lg">
                         This is a toy Monte Carlo  -  not a research framework. It does not attempt completeness. It selects the most important mechanisms so the learner can see them clearly. That simplification is not a weakness. It is the reason the project works.
                     </p>
                     <p className="mb-7 text-base font-light leading-relaxed text-gray-400 md:mb-8 md:text-lg">
                         Many learners start with deterministic school-physics instincts: one input, one output, one exact answer. Particle and detector physics behave differently. Beams scatter, signals fluctuate, measurements blur, and finite samples matter. A toy Monte Carlo makes that structure <strong className="text-white">visible</strong> without overwhelming the learner.
                     </p>
                     <ul className="space-y-3 font-mono text-xs text-gray-500 sm:text-sm">
                         <li className="flex items-center gap-3">
                             <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full shadow-[0_0_8px_#22d3ee]"></div>
                             <span>Spreads, peaks, tails, and fluctuations</span>
                         </li>
                         <li className="flex items-center gap-3">
                             <div className="w-1.5 h-1.5 bg-pink-400 rounded-full shadow-[0_0_8px_#f472b6]"></div>
                             <span>Knowledge from distributions, not single events</span>
                         </li>
                         <li className="flex items-center gap-3">
                             <div className="w-1.5 h-1.5 bg-lime-400 rounded-full shadow-[0_0_8px_#a3e635]"></div>
                             <span>Statistical thinking made visible</span>
                         </li>
                     </ul>
                </div>
                
                <div className="relative group">
                    <div className="absolute -inset-1 bg-cyan-400/20 rounded-lg blur opacity-0 group-hover:opacity-100 transition duration-500 group-hover:shadow-[0_0_30px_rgba(34,211,238,0.2)]"></div>
                    <div className="relative overflow-hidden rounded-lg transition-all duration-500 group-hover:-translate-y-2">
                      <img
                        src="/images/pic3.jpg"
                        alt="Student using the simulator on a laptop"
                        className="w-full h-auto rounded-lg object-cover"
                      />
                    </div>
                </div>
            </div>
            
            {/* Soft bottom blend  -  no hard edge */}
            <div className="pointer-events-none absolute bottom-0 left-0 right-0 z-30 h-[20vh] bg-gradient-to-t from-transparent via-transparent to-transparent" />
        </section>
    );
};

export default TheCalorimeter;
